import React, { useState, useEffect, useCallback, memo } from "react"
import { makeStyles } from "@material-ui/core/styles"
import Box from "@material-ui/core/Box"
import Typography from "@material-ui/core/Typography"
import TextField from "@material-ui/core/TextField"
import { emailFormatCheck } from "@/utils"
import useDebounce from "@/hooks/useDebounce"
import Button from "@material-ui/core/Button"
import { newUserAssignManagerRole } from "@/apis"
import AlertWithTimer from "@/components/common/AlertWithTimer"
import RequestSnackbar from "@/components/common/RequestSnackbar"
import useHandleRequestSnackbar from "@/hooks/useHandleRequestSnackbar"
import { useSelector } from "react-redux"

const useStyles = makeStyles((theme) => ({
    boxWrapper: {
        padding: theme.spacing(4, 2),
    },
}))

const initAddingManuallyFormStatus = {
    participantFirstName: {
        status: false,
        errorMessage: "",
    },
    participantLastName: { status: false, errorMessage: "" },
    participantEmail: { status: false, errorMessage: "" },
    overAll: false,
}

const inviteErrorInit = {
    status: false,
    errorMsg: "",
}

const InviteNewPerson = memo((props) => {
    const { userCompanyId } = props
    const classes = useStyles()
    const [isAddingManuallyFormInvalid, setIsAddingManuallyFormInvalid] = useState(initAddingManuallyFormStatus)
    const [newParticipantManuallyAddDetail, setNewParticipantManuallyAddDetail] = useState({
        participantFirstName: "",
        participantLastName: "",
        participantEmail: "",
    })
    const [newParticipantManuallyAddSuccess, setNewParticipantManuallyAddSuccess] = useState(false)
    const [inviteError, setInviteError] = useState(inviteErrorInit)
    const inviteSnackbarValue = useHandleRequestSnackbar(inviteError)
    const { user: userDetail } = useSelector((state) => state.userReducer)

    useEffect(() => {
        let timer
        if (newParticipantManuallyAddSuccess) {
            timer = setTimeout(() => {
                setNewParticipantManuallyAddSuccess(false)
            }, 3000)
        }
        return () => {
            clearTimeout(timer)
        }
    }, [newParticipantManuallyAddSuccess])

    const handleFormChange = useCallback(
        (event) => {
            setInviteError(inviteErrorInit)
            setNewParticipantManuallyAddSuccess(false)
            setIsAddingManuallyFormInvalid(initAddingManuallyFormStatus)
            setNewParticipantManuallyAddDetail((prev) => ({
                ...prev,
                [event.target.name]: event.target.value,
            }))
        },
        [isAddingManuallyFormInvalid, newParticipantManuallyAddDetail, newParticipantManuallyAddSuccess]
    )

    const validate = useCallback(({ value, key, extraCheck = null }) => {
        if (value === "") {
            setIsAddingManuallyFormInvalid((prev) => ({ ...prev, [key]: { ...prev[key], status: true }, overall: true }))
            return false
        }
        if (extraCheck === "email") {
            const emailFormatCheckStatus = emailFormatCheck(value)
            if (!emailFormatCheckStatus) {
                setIsAddingManuallyFormInvalid((prev) => ({
                    ...prev,
                    [key]: {
                        ...prev[key],
                        status: true,
                        errorMessage: "Email format is wrong",
                    },
                    overall: true,
                }))
                return false
            }
        }
        return true
    }, [])

    const handleManuallyAdd = useDebounce(async () => {
        const validateParticipantFirstName = validate({
            value: newParticipantManuallyAddDetail.participantFirstName,
            key: "participantFirstName",
        })
        const validateParticipantLastName = validate({
            value: newParticipantManuallyAddDetail.participantLastName,
            key: "participantLastName",
        })
        const validateParticipantEmail = validate({
            value: newParticipantManuallyAddDetail.participantEmail,
            key: "participantEmail",
            extraCheck: "email",
        })
        const validateStatus = validateParticipantFirstName && validateParticipantLastName && validateParticipantEmail
        if (validateStatus) {
            // add new user
            const newParticipant = {
                firstName: newParticipantManuallyAddDetail.participantFirstName,
                lastName: newParticipantManuallyAddDetail.participantLastName,
                email: newParticipantManuallyAddDetail.participantEmail,
            }
            const createUserName = userDetail.lastName ? `${userDetail.firstName} ${userDetail.lastName}` : userDetail.firstName
            try {
                await newUserAssignManagerRole(userCompanyId, newParticipant, createUserName)
                setNewParticipantManuallyAddSuccess(true)
                setNewParticipantManuallyAddDetail({
                    participantFirstName: "",
                    participantLastName: "",
                    participantEmail: "",
                })
            } catch (error) {
                console.log("error", error)
                setInviteError({
                    status: true,
                    errorMsg: error.data?.message || "Failed to add",
                })
            }
        }
    }, 500)

    return (
        <>
            <Box className={classes.boxWrapper}>
                <Typography variant="h6" component="div">
                    Invite A Project Manager
                </Typography>
                <Box display={{ xs: "block", sm: "flex" }} mt={1} mb={2}>
                    <Box mt={1} mr={1} display={{ xs: "block", sm: "inline" }}>
                        <TextField
                            required
                            label="First Name"
                            error={isAddingManuallyFormInvalid.participantFirstName.status}
                            helperText={isAddingManuallyFormInvalid.participantFirstName.status && "First name required"}
                            id="participant-first-name"
                            name="participantFirstName"
                            placeholder="Enter First Name"
                            variant="outlined"
                            onChange={handleFormChange}
                            value={newParticipantManuallyAddDetail.participantFirstName}
                            fullWidth
                        />
                    </Box>
                    <Box mt={1} mr={1} display={{ xs: "block", sm: "inline" }}>
                        <TextField
                            required
                            label="Last Name"
                            error={isAddingManuallyFormInvalid.participantLastName.status}
                            helperText={isAddingManuallyFormInvalid.participantLastName.status && "Last name required"}
                            id="participant-last-name"
                            name="participantLastName"
                            placeholder="Enter Last Name"
                            variant="outlined"
                            onChange={handleFormChange}
                            value={newParticipantManuallyAddDetail.participantLastName}
                            fullWidth
                        />
                    </Box>
                    <Box mt={1} mr={1} display={{ xs: "block", sm: "inline" }}>
                        <TextField
                            required
                            label="Email"
                            error={isAddingManuallyFormInvalid.participantEmail.status}
                            helperText={
                                isAddingManuallyFormInvalid.participantEmail.status &&
                                (isAddingManuallyFormInvalid.participantEmail.errorMessage
                                    ? isAddingManuallyFormInvalid.participantEmail.errorMessage
                                    : "Email required")
                            }
                            id="participant-email"
                            name="participantEmail"
                            placeholder="Enter Email"
                            variant="outlined"
                            onChange={handleFormChange}
                            value={newParticipantManuallyAddDetail.participantEmail}
                            fullWidth
                        />
                    </Box>
                </Box>
                <Box display={{ xs: "block", sm: "inline" }}>
                    <Button variant="contained" color="primary" onClick={handleManuallyAdd}>
                        Confirm
                    </Button>
                </Box>
                {newParticipantManuallyAddSuccess ? (
                    <Box color="success.main" mt={3}>
                        <Typography component="div" display="inline" ml={2}>
                            <AlertWithTimer severity="success">That Participant has been added.</AlertWithTimer>
                        </Typography>
                    </Box>
                ) : null}
            </Box>
            {inviteError.status && (
                <RequestSnackbar
                    status={false}
                    message={inviteError.errorMsg}
                    snackbarOpen={inviteSnackbarValue.snackbarOpen}
                    handleSnackbarOpenStatus={inviteSnackbarValue.handleSnackbarOpenStatus}
                />
            )}
        </>
    )
})

export default InviteNewPerson
